// Decides what support entry the page shows. With the composer flag on, a
// signed-in customer gets the composer when support is switched on for the web
// app and their company; otherwise (switched off or unreachable) the page keeps
// LiveChat. Once a signed-in customer has LiveChat it stays for the rest of that
// sign-in, so an open chat never disappears; the next sign-in or a reload checks
// again (phones, where LiveChat is hidden, retry a few times). Signed out, once
// sign-in has settled, the composer shows in visitor mode (text only) when the
// availability check says visitorEnabled, and LiveChat otherwise, until the next
// sign-in or sign-out. The composer's code loads only when it is shown. A support
// reply email's link (deepLink.js) waits for the composer, and is dropped where
// there will be none; signed out it waits for a sign-in, unless it is the
// visitor's own conversation (SupportComposer). A sign-in clears what a visitor
// left in the browser. Whether the composer is on screen is published
// (supportEntry) for the landing page footer's "Chat with us".
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { isNarrowMenuViewport } from 'utils/viewport';
import {
  addBreadcrumb,
  clearBreadcrumbs,
  installBreadcrumbListeners,
  sanitizePath,
} from './breadcrumbs';
import {
  forgetVisitor,
  noteSignedInUser,
  readVisitorConversation,
  writePendingConversation,
} from './conversationStore';
import { supportLinkFrom, withoutSupportLink } from './deepLink';
import { getAvailability } from './supportApi';
import { notifySupportPanelOpen, OPEN_EVENT, setComposerShown } from './supportEntry';
import { toggleMenuMobile } from '../store/actions/Menu';
import { toggleCart } from '../store/actions/Cart';

const FLAG_KEY = '@LT/SUPPORT_COMPOSER';
// checkedUser while signed out: the visitor check.
const VISITOR = '@visitor';
// Sign-in is restored after the first render; wait this long for it before showing LiveChat.
const AUTH_SETTLE_MS = 4000;
// Where LiveChat hides itself (phones), a failed check would leave no support at all: check again.
const PHONE_RETRY_MS = [15000, 60000, 180000];

// Same test as LiveChatLoader.isMobileSurface in App.js.
function liveChatHiddenHere() {
  const userAgent =
    (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  return (
    /Android|iPhone|iPad|iPod/i.test(userAgent) ||
    (typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(max-width: 767px)').matches)
  );
}

/**
 * Build flag REACT_APP_SUPPORT_COMPOSER=true, or per browser: localStorage '@LT/SUPPORT_COMPOSER'
 * = 'on' / 'off'. 'on' is honoured only in non-production builds (those built with REACT_APP_API_HOST);
 * 'off' always works.
 */
export function composerFlagOn() {
  let override = null;
  try {
    override = window.localStorage.getItem(FLAG_KEY);
  } catch (e) {
    override = null;
  }
  if (override === 'off') return false;
  if (override === 'on' && process.env.REACT_APP_API_HOST) return true;
  return process.env.REACT_APP_SUPPORT_COMPOSER === 'true';
}

export class SupportDock extends Component {
  // available: what the last check said, for the user in checkedUser (null while checking).
  state = {
    available: null,
    checkedUser: null,
    authTimedOut: false,
    Composer: null,
    // A visitor chat was in this browser when a customer signed in: the composer says where it went.
    visitorChatLeft: false,
  };
  flagOn = composerFlagOn();
  availabilityCheck = 0;

  componentDidMount() {
    window.addEventListener(OPEN_EVENT, this.onSupportRequested);
    if (this.flagOn) {
      installBreadcrumbListeners();
      addBreadcrumb('screen', sanitizePath(this.props.location.pathname));
      if (this.props.signedIn) noteSignedInUser(this.props.userId);
    } else {
      // No composer at all: a link has nowhere to open.
      writePendingConversation(null);
    }
    // A customer is signed in here: a visitor's chat and details (perhaps someone else's, on a shared
    // browser) go, whether or not the composer is on.
    if (this.props.signedIn) this.forgetVisitorForSignIn();
    this.authTimer = window.setTimeout(
      () => this.setState({ authTimedOut: true }),
      AUTH_SETTLE_MS
    );
    this.checkAvailability();
    setComposerShown(this.composerShown);
  }

  componentDidUpdate(prevProps, prevState) {
    // After the composer has mounted (children first), so it is listening for "Chat with us".
    setComposerShown(this.composerShown);
    if (
      this.flagOn &&
      prevProps.location.pathname !== this.props.location.pathname
    ) {
      addBreadcrumb('screen', sanitizePath(this.props.location.pathname));
    }
    // index.js reads the link as the page loads; one can still arrive later, in an address a
    // sign-in redirect carried it in (/?redirect=/Menu?support=...), or where an email sign-in
    // link goes after signing in (magicLink.js: the link was waiting in another tab).
    if (prevProps.location.search !== this.props.location.search) {
      this.takeSupportLink();
    }
    // Breadcrumbs from one account's session don't go with the next one's messages: sign-out, or a
    // switch without one (a magic link for another account opened in this tab). Stored conversations
    // and drafts are per user and survive a sign-out; noteSignedInUser clears them for another user.
    if (
      (prevProps.signedIn && !this.props.signedIn) ||
      (prevProps.userId &&
        this.props.userId &&
        prevProps.userId !== this.props.userId)
    ) {
      clearBreadcrumbs();
    }
    if (
      prevProps.signedIn !== this.props.signedIn ||
      prevProps.userId !== this.props.userId
    ) {
      if (this.flagOn && this.props.signedIn)
        noteSignedInUser(this.props.userId);
      if (this.props.signedIn) this.forgetVisitorForSignIn();
      this.checkAvailability();
    } else if (
      !this.props.signedIn &&
      this.authSettled() &&
      !(prevProps.authSettled || prevState.authTimedOut)
    ) {
      // Signed out, and sign-in has just settled: the visitor check can run.
      this.checkAvailability();
    }
  }

  authSettled() {
    return !!this.props.authSettled || this.state.authTimedOut;
  }

  componentWillUnmount() {
    window.removeEventListener(OPEN_EVENT, this.onSupportRequested);
    this.unmounted = true;
    setComposerShown(false);
    window.clearTimeout(this.authTimer);
    window.clearTimeout(this.retryTimer);
  }

  // Through the router, not history.replaceState as at load: its own copy of the address loses the
  // parameter too, so a later redirect (a sign-out on this page) can't carry it back.
  takeSupportLink() {
    const { location, history } = this.props;
    const conversationId = supportLinkFrom(location.search);
    if (!conversationId) return;
    // No composer at all: a link has nowhere to open.
    writePendingConversation(this.flagOn ? conversationId : null);
    history.replace({
      ...location,
      search: withoutSupportLink(location.search),
    });
  }

  // Once per sign-in: a failed check means LiveChat for the rest of it, except on phones, where
  // LiveChat is hidden and the check is retried a few times. Each check (and each sign-in change)
  // bumps the generation, so a late answer for an earlier user is ignored.
  checkAvailability(attempt = 0) {
    window.clearTimeout(this.retryTimer);
    const check = ++this.availabilityCheck;
    if (this.state.checkedUser !== null) {
      this.setState({ available: null, checkedUser: null });
    }
    if (!this.flagOn) return;
    if (!this.props.signedIn) {
      this.checkVisitor(check);
      return;
    }
    const userId = this.props.userId;
    const settle = (available, retrying) => {
      if (this.unmounted || check !== this.availabilityCheck) return;
      this.setState({ available, checkedUser: userId });
      if (available) this.loadComposer();
      // No composer for this sign-in: a link has nowhere to open.
      else if (!retrying) writePendingConversation(null);
    };
    getAvailability().then(
      status => settle(!!(status && status.enabled)),
      () => {
        const delay = PHONE_RETRY_MS[attempt];
        const retrying = !!delay && liveChatHiddenHere();
        settle(false, retrying);
        if (retrying && check === this.availabilityCheck) {
          this.retryTimer = window.setTimeout(() => {
            if (!this.unmounted && check === this.availabilityCheck)
              this.checkAvailability(attempt + 1);
          }, delay);
        }
      }
    );
  }

  // Signed out, once sign-in has settled (until then the page shows nothing, as before): the composer
  // in visitor mode when the check says visitorEnabled, otherwise LiveChat, with no retries. A link
  // waiting for a sign-in stays.
  checkVisitor(check) {
    if (!this.authSettled()) return;
    const settle = available => {
      if (this.unmounted || check !== this.availabilityCheck) return;
      this.setState({ available, checkedUser: VISITOR });
      if (available) this.loadComposer();
    };
    new Promise(resolve => resolve(getAvailability())).then(
      status => settle(!!(status && status.visitorEnabled)),
      () => settle(false)
    );
  }

  loadComposer() {
    if (this.state.Composer || this.loadingComposer) return;
    this.loadingComposer = true;
    import('./SupportComposer').then(
      module => {
        if (!this.unmounted) this.setState({ Composer: module.default });
      },
      () => {
        this.loadingComposer = false;
        // LiveChat instead, for the rest of this sign-in. Signed out, a link still waits for one.
        if (this.props.signedIn) writePendingConversation(null);
        if (!this.unmounted) this.setState({ available: false });
      }
    );
  }

  // Support was switched off after the composer appeared (a 403 on send or dictation): LiveChat for the
  // rest of this sign-in, as when the check says off.
  markUnavailable = () => {
    if (this.props.signedIn) writePendingConversation(null);
    this.setState({
      available: false,
      checkedUser: this.props.signedIn ? this.props.userId : VISITOR,
    });
  };

  render() {
    this.composerShown = false;
    return this.renderEntry();
  }

  renderEntry() {
    const { fallback, signedIn, authSettled, userId, location } = this.props;
    const { authTimedOut, Composer } = this.state;
    if (!this.flagOn) return this.showFallback(fallback);
    if (!signedIn && !authSettled && !authTimedOut) return null;
    // A result for another user, or for the visitor (or none yet), is still being checked.
    const available =
      this.state.checkedUser === (signedIn ? userId : VISITOR)
        ? this.state.available
        : null;
    if (available === false) return this.showFallback(fallback);
    if (!available || !Composer) {
      // While checking, LiveChat that is already on screen stays (an open chat isn't collapsed);
      // otherwise nothing, so LiveChat isn't loaded only to be replaced by the composer.
      return this.fallbackShown ? fallback || null : null;
    }
    this.fallbackShown = false;
    this.composerShown = true;
    if (!signedIn) {
      return (
        <Composer
          key="visitor"
          visitor
          pathname={location.pathname}
          onUnavailable={this.markUnavailable}
          onPanelOpen={this.closeMenusOnSmallScreens}
        />
      );
    }
    return (
      <Composer
        key={userId || 'user'}
        userId={userId}
        pathname={location.pathname}
        onUnavailable={this.markUnavailable}
        visitorChatLeft={this.state.visitorChatLeft}
        onVisitorChatNoteSeen={this.visitorChatNoteSeen}
        onPanelOpen={this.closeMenusOnSmallScreens}
      />
    );
  }

  // The visitor chat isn't carried over (the typed email proves nothing), but it isn't lost either: staff
  // reply to the email given in it. The note doesn't say whose chat it was.
  forgetVisitorForSignIn() {
    const hadChat = !!readVisitorConversation();
    forgetVisitor();
    if (hadChat) this.setState({ visitorChatLeft: true });
  }

  visitorChatNoteSeen = () => this.setState({ visitorChatLeft: false });

  // On a phone or tablet the menu and restaurants drawers sit above the composer (z-index 4999+): when the
  // conversation opens (the customer opened it, or a reply opened it) they close, so the conversation, the reply box
  // and Send are never under a drawer. The basket is left alone: it may hold a checkout.
  closeMenusOnSmallScreens = () => {
    if (!isNarrowMenuViewport()) return;
    if ((this.props.menuOpen || this.props.restaurantsOpen) && this.props.onCloseMenus) {
      this.props.onCloseMenus();
    }
    // The Categories sheet is page state (DefaultMenu, DynamicMenu): it listens for this.
    notifySupportPanelOpen();
  };

  // "Chat with us" or "Contact support" (a checkout error's reference): the customer asked for support, so on a
  // small screen the basket closes too.
  onSupportRequested = () => {
    if (!isNarrowMenuViewport()) return;
    if (this.props.basketOpen && this.props.onCloseBasket) this.props.onCloseBasket();
    this.closeMenusOnSmallScreens();
  };

  showFallback(fallback) {
    this.fallbackShown = !!fallback;
    return fallback || null;
  }
}

const mapStateToProps = state => {
  const user =
    state.authenticationReducer && state.authenticationReducer.userDetails;
  return {
    signedIn: !!user,
    userId: user ? user.idUser : null,
    authSettled:
      !!user ||
      !(state.signupReducer && state.signupReducer.needToTryAutomaticLogin),
    menuOpen: !!(state.menuReducer && state.menuReducer.showMenuMobile),
    restaurantsOpen: !!(state.menuReducer && state.menuReducer.showRestaurantsMobile),
    basketOpen: !!(state.cartReducer && state.cartReducer.cart && state.cartReducer.cart.checkout),
  };
};

// toggleMenuMobile(false) closes both the menu and the restaurants drawer (menuReducer).
const mapDispatchToProps = dispatch => ({
  onCloseMenus: () => dispatch(toggleMenuMobile(false)),
  onCloseBasket: () => dispatch(toggleCart(false)),
});

// No sign-out from support: a 401 from the support API only stops the composer (see SupportComposer).
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SupportDock));



// WEBPACK FOOTER //
// ./src/support/SupportDock.js